/**
 * Questionnaire helpers — multi-category and legacy single `category` field.
 */

import { getStatusSurface } from './appearanceTokens';

export function questionCategories(q: {
  category?: string | null;
  categories?: string[] | null;
}): string[] {
  if (Array.isArray(q.categories) && q.categories.length > 0) {
    const u = Array.from(new Set(q.categories.map(c => String(c).trim()).filter(Boolean)));
    if (u.length) return u;
  }
  const c = String(q.category ?? 'General').trim() || 'General';
  return [c];
}

export function primaryCategory(q: { category?: string | null; categories?: string[] | null }): string {
  return questionCategories(q)[0];
}

/** All distinct category names from a list of questions (for filter chips / autocomplete). */
export function allCategoryNames(
  questions: Array<{ category?: string | null; categories?: string[] | null }>
): string[] {
  const set = new Set<string>();
  questions.forEach(q => questionCategories(q).forEach(c => set.add(c)));
  return Array.from(set).sort();
}

/** First non-empty row value matching one of the header names (case-insensitive). */
export function getExcelCell(row: Record<string, unknown>, ...headerNames: string[]): unknown {
  const keys = Object.keys(row);
  for (const want of headerNames) {
    const exact = keys.find(k => k === want);
    if (exact !== undefined && row[exact] != null && String(row[exact]).trim() !== '') return row[exact];
    const lower = want.toLowerCase();
    const ci = keys.find(k => k.toLowerCase() === lower);
    if (ci !== undefined && row[ci] != null && String(row[ci]).trim() !== '') return row[ci];
  }
  return '';
}

/**
 * Build dropdown/multi-select options from an Excel cell.
 * Template uses `Yes | No | Partial | N/A` (pipe-separated). Also supports newline, `;`, or `,`.
 */
export function parsePipeDelimitedOptions(raw: unknown): { value: string; label: string }[] | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return parsePipeDelimitedOptions(String(raw));
  if (Array.isArray(raw)) {
    if (
      raw.length &&
      typeof raw[0] === 'object' &&
      raw[0] !== null &&
      ('value' in (raw[0] as object) || 'label' in (raw[0] as object))
    ) {
      return raw as { value: string; label: string }[];
    }
    const flat = raw.map(x => String(x).trim()).filter(Boolean);
    if (!flat.length) return null;
    if (flat.length > 1 && flat.every(s => s.length === 1)) {
      const merged = flat.join('');
      if (merged.includes('|')) return parsePipeDelimitedOptions(merged);
    }
    return flat.map(v => ({ value: v, label: v }));
  }
  const s = String(raw).trim().replace(/\r\n/g, '\n');
  if (!s) return null;
  let parts: string[] = [];
  if (s.includes('|')) {
    parts = s.split('|').map(p => p.trim()).filter(Boolean);
  } else if (s.includes('\n')) {
    parts = s.split(/\n/).map(p => p.trim()).filter(Boolean);
  } else if (s.includes(';')) {
    parts = s.split(';').map(p => p.trim()).filter(Boolean);
  } else {
    parts = s.split(',').map(p => p.trim()).filter(Boolean);
  }
  if (!parts.length) return null;
  return parts.map(p => ({ value: p, label: p }));
}

/** Trim + lowercase for matching framework tags across Excel/API/UI. */
export function normalizeFrameworkLabel(s: string): string {
  return String(s ?? '').trim().toLowerCase();
}

/** Framework tags on a question (trimmed, non-empty). */
export function questionFrameworkTags(q: { applicable_frameworks?: string[] | null }): string[] {
  return (q.applicable_frameworks || []).map((f) => String(f).trim()).filter(Boolean);
}

/** Filter-bar sentinel for questions with no framework tags. */
export const FRAMEWORK_FILTER_UNTAGGED = '__untagged__';

const FRAMEWORK_CHIP_COLORS = [
  { kind: 'info' as const },
  { kind: 'purple' as const },
  { kind: 'success' as const },
  { kind: 'warning' as const },
  { kind: 'pink' as const },
  { kind: 'teal' as const },
  { kind: 'purple' as const },
  { kind: 'neutral' as const },
] as const;

function hashFrameworkLabel(label: string): number {
  const s = normalizeFrameworkLabel(label);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Stable multicolor chip styles per framework label. */
export function getFrameworkChipSx(
  label: string,
  opts?: { selected?: boolean; maxWidth?: number; height?: number; fontSize?: string; mode?: 'light' | 'dark' }
): Record<string, unknown> {
  const mode = opts?.mode ?? 'light';
  const meta = FRAMEWORK_CHIP_COLORS[hashFrameworkLabel(label) % FRAMEWORK_CHIP_COLORS.length];
  const surface = getStatusSurface(meta.kind, mode);
  const selected = opts?.selected ?? false;
  return {
    height: opts?.height ?? 20,
    fontSize: opts?.fontSize ?? '0.65rem',
    maxWidth: opts?.maxWidth ?? 140,
    fontWeight: selected ? 700 : 600,
    bgcolor: selected ? surface.fg : surface.bg,
    color: selected ? (mode === 'dark' ? '#121212' : '#fff') : surface.fg,
    border: `1px solid ${surface.border}`,
    '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
  };
}

/** Table list filter (filter bar); empty `selected` = show all. */
export function questionMatchesTableFrameworkFilter(
  q: { applicable_frameworks?: string[] | null },
  selected: string[]
): boolean {
  if (selected.length === 0) return true;
  const tags = questionFrameworkTags(q);
  const wantUntagged = selected.includes(FRAMEWORK_FILTER_UNTAGGED);
  const fwSel = selected.filter((s) => s !== FRAMEWORK_FILTER_UNTAGGED);
  if (wantUntagged && tags.length === 0) return true;
  if (fwSel.length === 0) return wantUntagged;
  const selNorm = new Set(fwSel.map(normalizeFrameworkLabel));
  return tags.some((t) => selNorm.has(normalizeFrameworkLabel(t)));
}

/** Distinct framework labels from questions, deduped by normalized name (first spelling wins). */
export function distinctFrameworkLabelsFromQuestions(
  questions: Array<{ applicable_frameworks?: string[] | null }>
): string[] {
  const byNorm = new Map<string, string>();
  questions.forEach((q) => {
    (q.applicable_frameworks || []).forEach((f) => {
      const display = String(f).trim();
      if (!display) return;
      const key = normalizeFrameworkLabel(display);
      if (!byNorm.has(key)) byNorm.set(key, display);
    });
  });
  return Array.from(byNorm.values()).sort((a, b) => a.localeCompare(b));
}

/** True if `frameworkFilter` overlaps question tags (normalized). Untagged questions always match (same as application-owner scope). */
export function questionMatchesSelectedFrameworks(
  q: { applicable_frameworks?: string[] | null },
  frameworkFilter: string[],
  catalog: string[]
): boolean {
  if (!catalog.length) return true;
  const tags = (q.applicable_frameworks || []).map((f) => String(f).trim()).filter(Boolean);
  if (tags.length === 0) return true;

  const active = frameworkFilter.length > 0 ? frameworkFilter : catalog;
  const selectedNorm = new Set(active.map(normalizeFrameworkLabel));
  return tags.some((t) => selectedNorm.has(normalizeFrameworkLabel(t)));
}

/** True when options were lost to `String(array)` / join / bad persistence. */
function isCorruptedOptionsString(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  if (/\[object Object\]/i.test(t)) return true;
  if (/object Object/i.test(t) && /,/.test(t)) return true;
  return false;
}

/** Edit dialog: JSON / object[] → one label per line for the options textarea. */
export function responseOptionsToFormString(opts: unknown): string {
  if (opts == null || opts === '') return '';
  if (typeof opts === 'string') {
    const raw = opts.trim();
    if (isCorruptedOptionsString(raw)) return '';
    try {
      return responseOptionsToFormString(JSON.parse(raw));
    } catch {
      if (raw.includes('|') && !raw.includes('\n')) {
        const piped = parsePipeDelimitedOptions(raw);
        if (piped?.length) return piped.map(o => o.label || o.value).join('\n');
      }
      return opts;
    }
  }
  if (Array.isArray(opts)) {
    const asStrings = opts.map(o => {
      if (typeof o === 'string') return o;
      if (o && typeof o === 'object') {
        const r = o as Record<string, unknown>;
        if (r.label != null) return String(r.label);
        if (r.value != null) return String(r.value);
        if (r.text != null) return String(r.text);
        if (r.name != null) return String(r.name);
      }
      return '';
    });
    if (asStrings.length > 1 && asStrings.every(s => s.length === 1)) {
      const merged = asStrings.join('');
      if (merged.includes('|')) {
        const repaired = parsePipeDelimitedOptions(merged);
        if (repaired?.length) return repaired.map(o => o.label || o.value).join('\n');
      }
    }
    return opts
      .map(o => {
        if (typeof o === 'string') return o;
        if (o && typeof o === 'object') {
          const r = o as Record<string, unknown>;
          if (r.label != null) return String(r.label);
          if (r.value != null) return String(r.value);
          if (r.text != null) return String(r.text);
          if (r.name != null) return String(r.name);
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof opts === 'object' && opts !== null) {
    const r = opts as Record<string, unknown>;
    if (Array.isArray(r.options)) return responseOptionsToFormString(r.options);
    if (Array.isArray(r.choices)) return responseOptionsToFormString(r.choices);
  }
  return '';
}

/** Safe value for a controlled options textarea — never pass a raw array/object. */
export function responseOptionsInputValue(opts: unknown): string {
  if (opts == null || opts === '') return '';
  if (typeof opts === 'string') return responseOptionsToFormString(opts);
  return responseOptionsToFormString(opts);
}

/** Save: textarea lines → [{ value, label }] for dropdown / multi_select; otherwise null. */
export function responseOptionsRawToText(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') {
    if (isCorruptedOptionsString(raw)) return '';
    return raw;
  }
  return responseOptionsToFormString(raw);
}

export function responseOptionsFromFormLines(
  responseType: string,
  raw: unknown
): { value: string; label: string }[] | null {
  if (responseType !== 'dropdown' && responseType !== 'multi_select') return null;
  const text = responseOptionsRawToText(raw);
  const lines = text
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);
  if (!lines.length) return null;
  if (lines.length === 1 && lines[0].includes('|')) {
    return parsePipeDelimitedOptions(lines[0]);
  }
  return lines.map(v => ({ value: v, label: v }));
}
